const DATE_PARTS = {
  dicom: /^(\d{4})(\d{2})(\d{2})$/,
  yearFirst: /^(\d{4})[-./](\d{2})[-./](\d{2})(?:[T\s].*)?$/,
  dayFirst: /^(\d{2})[-./](\d{2})[-./](\d{4})$/,
};

type DateParts = {
  day: string;
  month: string;
  year: string;
};

function isValidDateParts({ day, month, year }: DateParts) {
  const numericYear = Number(year);
  const numericMonth = Number(month);
  const numericDay = Number(day);
  const candidate = new Date(Date.UTC(numericYear, numericMonth - 1, numericDay));

  return (
    candidate.getUTCFullYear() === numericYear &&
    candidate.getUTCMonth() === numericMonth - 1 &&
    candidate.getUTCDate() === numericDay
  );
}

function getDateParts(value: string): DateParts | null {
  const trimmed = value.trim();
  const dayFirstMatch = trimmed.match(DATE_PARTS.dayFirst);
  if (dayFirstMatch) {
    return { day: dayFirstMatch[1], month: dayFirstMatch[2], year: dayFirstMatch[3] };
  }

  const dicomMatch = trimmed.match(DATE_PARTS.dicom);
  if (dicomMatch) {
    return { day: dicomMatch[3], month: dicomMatch[2], year: dicomMatch[1] };
  }

  const yearFirstMatch = trimmed.match(DATE_PARTS.yearFirst);
  if (yearFirstMatch) {
    return { day: yearFirstMatch[3], month: yearFirstMatch[2], year: yearFirstMatch[1] };
  }

  return null;
}

/** Formats a user-visible date without allowing locale-specific slash separators. */
export function formatDateDDMMYYYY(value: string | null | undefined, fallback = '-') {
  if (!value) return fallback;
  const parts = getDateParts(value);
  if (!parts || !isValidDateParts(parts)) return value;
  return `${parts.day}.${parts.month}.${parts.year}`;
}

/** Converts the UI date back to the API's ISO date-only representation. */
export function toApiDate(value: string) {
  const parts = getDateParts(value);
  if (!parts || !isValidDateParts(parts)) return value.trim();
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** Adds DD.MM.YYYY separators while keeping partially entered values editable. */
export function maskDateInput(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join('.');
}
